import { Link, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";

import { loginFn } from "src/api/auth";
import { useAuth } from "src/providers/AuthProvider";
import AuthField from "src/components/auth/AuthField";
import AuthHeading from "src/components/auth/AuthHeading";
import { Button } from "src/components/ui/button";
import { setToLocalStorage, showSuccessToast } from "src/lib/utils";

const validationSchema = Yup.object().shape({
	email: Yup.string().email("Please provide a valid email").required("Email is required"),
	password: Yup.string().required("Password is required"),
});

const Login = () => {
	const { setUser } = useAuth();
	const navigate = useNavigate();

	const { mutate, isLoading } = useMutation(loginFn, {
		onSuccess: ({ message, data }, { email }) => {
			showSuccessToast(message ?? "Logged in successfully");

			const user = data?.user ?? null;

			if (!user?.is_otp_verified) {
				navigate("/otp-verification", { state: { email } });
				return;
			}

			setUser(user);
			setToLocalStorage("access_token", data?.access_token);
			navigate("/dashboard");
		},
	});

	return (
		<>
			<AuthHeading title="Welcome back" subtitle="Log in to your Paylinq account." />

			<Formik initialValues={{ email: "", password: "" }} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
				{({ values, handleChange, handleBlur, handleSubmit, errors, touched }) => (
					<form onSubmit={handleSubmit} className="tw-space-y-4">
						<AuthField
							label="Email"
							name="email"
							type="email"
							placeholder="you@business.com"
							value={values.email}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.email && errors.email}
							autoComplete="email"
						/>

						<AuthField
							label="Password"
							name="password"
							isPassword
							placeholder="Enter your password"
							value={values.password}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.password && errors.password}
							autoComplete="current-password"
						/>

						<div className="tw-flex tw-justify-end">
							<Link to="/forgot-password" className="tw-text-[13px] tw-font-semibold tw-text-teal hover:tw-underline">
								Forgot password?
							</Link>
						</div>

						<Button type="submit" disabled={isLoading} className="tw-w-full tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
							{isLoading ? "Logging in..." : "Log in"}
						</Button>

						<p className="tw-text-center tw-text-[13px] tw-text-text-soft">
							Not registered?{" "}
							<Link to="/signup" className="tw-font-semibold tw-text-teal hover:tw-underline">
								Create an account
							</Link>
						</p>
					</form>
				)}
			</Formik>
		</>
	);
};

export default Login;
